import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import dropzoneIcon from '@/assets/images/dropzone-icon.png?url';
import { Button } from '../atoms/button';
import { Spinner } from '../atoms/spinner';

interface DropzoneSingleProps {
  handleUpload?: (file: File, onProgress: (progress: number) => void) => Promise<void>;
  onDone?: (file: File) => void;
  // isUploading?: boolean;
  isUploadingFile?: {
    file: File;
    progress: number;
  } | null;
  onDrop?: (file: File) => void; // New prop for immediate file handling without upload
  hasFile?: boolean;
}

export function DropzoneSingle(props: DropzoneSingleProps) {
  const { handleUpload, onDone, onDrop, hasFile = false, isUploadingFile } = props;
  const [uploadingFile, setUploadingFile] = useState<{
    file: File;
    progress: number;
  } | null>(isUploadingFile ?? null);

  // TODO: this is used to update uploadingFile progress from props
  // It's necessary because we can have "external" uploadingFile progress getting updated
  // and we have internal uploadingFile when we don't need to control outside upload
  useEffect(() => {
    if (isUploadingFile) {
      setUploadingFile(isUploadingFile);
    }
  }, [isUploadingFile]);

  const onDropCallback = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      const file = acceptedFiles[0];

      // If onDrop is provided, just call it (for storing file without upload)
      if (onDrop) {
        onDrop(file);
        return;
      }

      // Otherwise, proceed with upload (original behavior)
      setUploadingFile({
        file,
        progress: 0,
      });

      try {
        await handleUpload?.(file, (progress) => {
          setUploadingFile((prev) => (prev ? { ...prev, progress } : null));
        });

        onDone?.(file);
        setUploadingFile(null);
      } catch (error) {
        console.error('Error uploading file:', error);
        setUploadingFile(null);
      }
    },
    [onDrop, handleUpload, onDone],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropCallback,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    disabled: !!isUploadingFile,
  });

  // Hide the dropzone completely when uploading or when a file is already selected/uploaded
  if (isUploadingFile?.file) {
    return (
      <div
        className="w-full h-[10.31rem] border-2 border-dashed rounded-md cursor-pointer
        flex items-center justify-center text-center transition-colors border-brand-800"
      >
        <div className="flex justify-between text-sm text-primary-700 gap-3">
          <Spinner content={uploadingFile?.progress ? `${uploadingFile?.progress}%` : ''} />
          <div className="flex flex-col gap-2">
            <span>Uploading your CSV file</span>
            <span className="flex h-full">
              <Button variant="outline" size="sm">
                Cancel
              </Button>
            </span>
          </div>
        </div>
      </div>
    );
  }
  if (hasFile) return null;

  return (
    <div
      {...getRootProps({
        className: `w-full h-[10.31rem] border-2 border-dashed rounded-md cursor-pointer
        flex items-center justify-center text-center transition-colors
        ${
          isDragActive
            ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
            : 'border-brand-800 dark:border-primary-800'
        }`,
      })}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center gap-2 pointer-events-none">
        <img
          src={dropzoneIcon}
          className="size-9"
          alt="CSV dropzone logo"
          aria-label="CSV dropzone logo"
        />
        {isDragActive ? (
          <div className="text-primary-700 text-base">Drop the file here</div>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <span className="text-primary-700 text-base">Select a CSV file to import</span>
            <span className="text-primary-600 text-sm">
              File with up to 5000 locations works the best
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
